import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DEPARTMENT_DEFS,
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_MAP,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_MAP,
  JOB_POST_STATUSES,
  JOB_POST_STATUS_MAP,
  MAX_JOB_BULLETS,
  WORK_MODES,
  WORK_MODE_MAP,
  careersUrl,
  jobApplyUrl,
  type EducationLevel,
  type EmploymentType,
  type JobPostStatus,
  type WorkMode,
} from "@goyone/shared";
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  SearchBox,
} from "../../components/ui";
import { Icon } from "../../components/Icon";
import { Fact, FactGrid } from "../../components/staff";
import { useToast } from "../../components/Toast";
import { api, qs } from "../../lib/api";
import { date, money, num, plural, WORDS } from "../../lib/format";
import { useDebounced, useListState } from "../../lib/hooks";
import { useAuth, useSession } from "../../auth/AuthProvider";

interface JobRow {
  id: string;
  code: string | null;
  title: string;
  deptKey: string | null;
  department: string | null;
  employmentType: string;
  employmentLabel: string;
  workMode: string | null;
  workModeLabel: string | null;
  location: string | null;
  openings: number;
  status: JobPostStatus;
  statusLabel: string;
  statusKind: string;
  accepting: boolean;
  slug: string;
  closesOn: string | null;
  openedAt: string | null;
  managerId: string | null;
  positionId: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  showSalary: boolean;
  campaign: { id: string; name: string } | null;
  form: { id: string; name: string } | null;
  applicationCount: number;
  activeCount: number;
  hiredCount: number;
  filled: boolean;
  /** عددُ النواقص المانعة للنشر — فارغُه يعني أنّ الصفَّ لم يحمل ما يُحسب منه. */
  missing: number | null;
}

interface JobGap {
  field: string;
  label: string;
  blocking: boolean;
  why: string;
}

interface JobDetailView extends JobRow {
  description: string | null;
  duties: string[];
  techRequirements: string[];
  softRequirements: string[];
  benefits: string[];
  languages: string[];
  experienceMin: number | null;
  education: string | null;
  educationLabel: string | null;
  gradeId: string | null;
  orgUnitId: string | null;
  scheduleId: string | null;
  costCenterId: string | null;
  position: { id: string; title: string } | null;
  grade: {
    id: string;
    name: string;
    minSalary: number | null;
    midSalary: number | null;
    maxSalary: number | null;
  } | null;
  orgUnit: { id: string; name: string } | null;
  schedule: { id: string; name: string } | null;
  costCenter: { id: string; code: string; name: string } | null;
  gaps: JobGap[];
  ready: boolean;
  formFields: {
    id: string;
    key: string;
    label: string;
    type: string;
    required: boolean;
  }[];
}

interface CampaignLite {
  id: string;
  name: string;
}

interface FormLite {
  id: string;
  name: string;
  isDefault: boolean;
}

interface PositionLite {
  id: string;
  title: string;
  department: string | null;
  grade: { id: string; name: string } | null;
  orgUnit: { id: string; name: string } | null;
}

interface PositionFull extends PositionLite {
  purpose: string | null;
  duties: string[];
  requirements: string[];
  deptKey: string | null;
}

interface GradeLite {
  id: string;
  name: string;
  minSalary: number | null;
  midSalary: number | null;
  maxSalary: number | null;
}

interface UnitOption {
  id: string;
  name: string;
  path: string;
}

interface PlanLite {
  id: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

interface CenterLite {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

/* ==========================================================================
   قائمة الوظائف
   ========================================================================== */

export function Jobs() {
  const { atLeast } = useAuth();
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? "");
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);

  const query = {
    status: state.status || undefined,
    campaignId: state.campaignId || undefined,
    q: debounced || undefined,
  };

  const list = useQuery({
    queryKey: ["recruit", "jobs", query],
    queryFn: () => api<JobRow[]>(`/hr/recruit/jobs${qs(query)}`),
  });

  const campaigns = useQuery({
    queryKey: ["recruit", "campaigns"],
    queryFn: () => api<CampaignLite[]>("/hr/recruit/campaigns"),
  });

  const rows = list.data ?? [];
  const open = rows.filter((j) => j.status === "OPEN");
  const incomplete = rows.filter((j) => (j.missing ?? 0) > 0);

  return (
    <div className="page">
      <PageHead
        title="الوظائف"
        subtitle={
          list.data
            ? `${plural(open.length, WORDS.job)} مفتوحة من ${plural(rows.length, WORDS.job)}`
            : undefined
        }
        actions={
          <>
            <CareersLink />
            {atLeast("MANAGER") && (
              <button
                className="btn btn-primary"
                onClick={() => setCreating(true)}
              >
                <Icon name="plus" size={15} />
                وظيفة جديدة
              </button>
            )}
          </>
        }
      />

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالمسمّى أو الكود"
        />

        <select
          className="select"
          value={state.status ?? ""}
          onChange={(e) => patch({ status: e.target.value || undefined })}
        >
          <option value="">كل الحالات</option>
          {JOB_POST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {JOB_POST_STATUS_MAP[s].label}
            </option>
          ))}
        </select>

        <select
          className="select"
          value={state.campaignId ?? ""}
          onChange={(e) => patch({ campaignId: e.target.value || undefined })}
        >
          <option value="">كل الحملات</option>
          {(campaigns.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {/*
        النقصُ يُقال في القائمة لا يُكتشف عند النشر: مسوّدةٌ ناقصةٌ بين
        عشرين وظيفةً لا تُميَّز بالنظر، فتبقى شهرًا ويُظنّ أنّها منشورة.
      */}
      {incomplete.length > 0 && (
        <Notice title="وظائفُ لا تُنشر حتى تكتمل" icon="alert" kind="warning">
          {plural(incomplete.length, WORDS.job)} ينقصها ما لا يستقيم إعلانٌ
          بدونه — الوصفُ أو المهامُّ أو المتطلّباتُ التقنيّة. افتح الوظيفةَ لترى
          ما ينقصها بالاسم.
        </Notice>
      )}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا وظائف بعد"
          text="الوظيفة تُكتب مسوّدةً أوّلًا، ثم تُنشر فيعمل رابط التقديم."
          action={
            atLeast("MANAGER") ? (
              <button
                className="btn btn-primary"
                onClick={() => setCreating(true)}
              >
                أنشئ وظيفة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th>الوظيفة</th>
                  <th>القسم</th>
                  <th>النوع</th>
                  <th>نطاق الراتب</th>
                  <th>الشواغر</th>
                  <th>الطلبات</th>
                  <th>الحملة</th>
                  <th>تُغلق</th>
                  <th>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((j) => (
                  <tr key={j.id}>
                    <td>
                      <Link
                        to={`/hr/recruit/jobs/${j.id}`}
                        className="cell-strong link"
                      >
                        {j.title}
                      </Link>
                      <div className="tiny mute">
                        {j.code ?? "—"}
                        {j.location ? ` · ${j.location}` : ""}
                        {j.workModeLabel ? ` · ${j.workModeLabel}` : ""}
                      </div>
                    </td>
                    <td className="cell-mute">{j.department ?? "—"}</td>
                    <td className="cell-mute">{j.employmentLabel}</td>
                    <td className="num cell-mute">
                      {salaryText(j.salaryMin, j.salaryMax)}
                      {!j.showSalary && (j.salaryMin || j.salaryMax) && (
                        <div className="tiny mute">لا يُعلَن</div>
                      )}
                    </td>
                    <td className="num">
                      {num(j.hiredCount)} / {num(j.openings)}
                      {j.filled && <div className="tiny mute">امتلأ</div>}
                    </td>
                    <td className="num">
                      {num(j.activeCount)}
                      {j.applicationCount !== j.activeCount && (
                        <div className="tiny mute">
                          من {num(j.applicationCount)}
                        </div>
                      )}
                    </td>
                    <td className="cell-mute">{j.campaign?.name ?? "—"}</td>
                    <td className="num cell-mute">
                      {j.closesOn ? date(j.closesOn) : "—"}
                    </td>
                    <td>
                      <Badge kind={j.statusKind}>{j.statusLabel}</Badge>
                      {(j.missing ?? 0) > 0 && (
                        <div className="mt-1">
                          <Badge kind="warning">
                            ينقصها {num(j.missing ?? 0)}
                          </Badge>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <JobDialog job={null} onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * رابطُ صفحة وظائف الشركة.
 *
 * ## ولماذا زرٌّ في ترويسة الوظائف
 *
 * روابطُ الوظائف تُنشر مفرّقةً، فمن رأى إعلانًا لوظيفةٍ لا تناسبه لا يعرف
 * أنّ عند الشركة غيرَها. وهذا الرابطُ ثابتٌ يُكتب مرّةً في توقيع البريد
 * وفي صفحة الشركة على المنصّات، ويبقى صالحًا بعد أن تُغلق كلُّ وظيفةٍ فيه
 * اليوم — فموضعُه حيث تُدار الوظائف لا في ركنٍ من الإعدادات.
 */
function CareersLink() {
  const session = useSession();
  const toast = useToast();
  const url = careersUrl(window.location.origin, session.tenant.slug);

  return (
    <>
      <button
        className="btn"
        onClick={() => {
          void navigator.clipboard.writeText(url);
          toast("نُسخ رابط صفحة الوظائف");
        }}
        title={url}
      >
        <Icon name="paperclip" size={15} />
        رابط صفحة الوظائف
      </button>
      <a className="btn" href={url} target="_blank" rel="noreferrer">
        <Icon name="eye" size={15} />
        معاينة
      </a>
    </>
  );
}

/** نطاقُ الراتب مقروءًا — وحدٌّ واحدٌ يُقرأ «من» أو «حتى» لا نصفَ نطاق. */
function salaryText(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return `${money(min)} — ${money(max)}`;
  if (min !== null) return `من ${money(min)}`;
  if (max !== null) return `حتى ${money(max)}`;
  return "—";
}

/* ==========================================================================
   تفصيل الوظيفة
   ========================================================================== */

export function JobDetail() {
  const { id = "" } = useParams();
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);

  const job = useQuery({
    queryKey: ["recruit", "job", id],
    queryFn: () => api<JobDetailView>(`/hr/recruit/jobs/${id}`),
  });

  const setStatus = useMutation({
    mutationFn: (status: JobPostStatus) =>
      api(`/hr/recruit/jobs/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast("تغيّرت حالة الوظيفة");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التغيير"),
  });

  const j = job.data;
  const blocking = (j?.gaps ?? []).filter((g) => g.blocking);
  const soft = (j?.gaps ?? []).filter((g) => !g.blocking);

  return (
    <div className="page">
      <PageHead
        title={j?.title ?? "الوظيفة"}
        subtitle={j ? `${j.code ?? ""} ${j.employmentLabel}`.trim() : undefined}
        actions={
          j &&
          atLeast("MANAGER") && (
            <>
              <button className="btn" onClick={() => setEditing(true)}>
                <Icon name="edit" size={15} />
                تعديل
              </button>
              {j.status !== "OPEN" && (
                <button
                  className="btn btn-primary"
                  onClick={() => setStatus.mutate("OPEN")}
                  /* الزرُّ يُطفأ ويُقال لماذا — ونداءٌ يُردّ بعد نقرةٍ أسوأ. */
                  disabled={setStatus.isPending || !j.ready}
                  title={j.ready ? undefined : "ينقص الملفَّ ما لا يُنشر بدونه"}
                >
                  نشر
                </button>
              )}
              {j.status === "OPEN" && (
                <button
                  className="btn"
                  onClick={() => setStatus.mutate("PAUSED")}
                  disabled={setStatus.isPending}
                >
                  إيقاف التقديم
                </button>
              )}
            </>
          )
        }
      />

      {job.isError && (
        <ErrorState error={job.error} onRetry={() => void job.refetch()} />
      )}

      {job.isPending && <div className="skeleton" style={{ height: 220 }} />}

      {j && (
        <>
          <div className="card">
            <div className="row" style={{ gap: "var(--sp-2)" }}>
              <Badge kind={j.statusKind}>{j.statusLabel}</Badge>
              {j.filled && <Badge kind="success">امتلأ الشاغر</Badge>}
              {j.campaign && (
                <Link className="link tiny" to="/hr/recruit/campaigns">
                  {j.campaign.name}
                </Link>
              )}
            </div>

            <p className="tiny mute mt-2">
              {JOB_POST_STATUS_MAP[j.status].description}
            </p>

            <div className="mt-4">
              <FactGrid>
                <Fact
                  icon="building"
                  label="القسم"
                  value={j.department ?? "—"}
                />
                <Fact
                  icon="mapPin"
                  label="مكان العمل"
                  value={
                    j.workModeLabel
                      ? `${j.location ?? "—"} · ${j.workModeLabel}`
                      : (j.location ?? "—")
                  }
                />
                <Fact
                  icon="wallet"
                  label="نطاق الراتب"
                  value={salaryText(j.salaryMin, j.salaryMax)}
                  title={
                    j.showSalary
                      ? "معلَنٌ في صفحة التقديم"
                      : "داخليٌّ لا يُعلَن"
                  }
                />
                <Fact
                  icon="users"
                  label="الشواغر"
                  value={`${num(j.hiredCount)} / ${num(j.openings)}`}
                />
                <Fact
                  icon="funnel"
                  label="الطلبات القائمة"
                  value={num(j.activeCount)}
                />
                <Fact
                  icon="calendar"
                  label="تُغلق في"
                  value={j.closesOn ? date(j.closesOn) : "—"}
                />
                <Fact
                  icon="clipboard"
                  label="الاستمارة"
                  value={j.form?.name ?? "الهويّة وحدها"}
                />
                <Fact
                  icon="briefcase"
                  label="الخبرة"
                  value={
                    j.experienceMin === null
                      ? "لا تُشترط"
                      : `${num(j.experienceMin)} سنة فأكثر`
                  }
                />
                <Fact
                  icon="book"
                  label="المؤهّل"
                  value={j.educationLabel ?? "—"}
                />
              </FactGrid>
            </div>
          </div>

          {/*
            النقصُ يُعرض بالاسم ويُقال أثرُه، لا «الملفُّ غير مكتمل» وحدَها:
            من يقرأ جملةً عامّةً يفتح النموذجَ ويبحث عن الخانة الفارغة.
          */}
          {j.gaps.length > 0 && (
            <Notice
              title={
                blocking.length > 0
                  ? "لا تُنشر حتى تكتمل"
                  : "تُنشر، وفيها ما ينقص"
              }
              icon={blocking.length > 0 ? "alert" : "info"}
              kind={blocking.length > 0 ? "warning" : undefined}
            >
              <ul style={{ margin: 0, paddingInlineStart: "1.1rem" }}>
                {blocking.map((g) => (
                  <li key={g.field}>
                    <strong>{g.label}</strong> — {g.why}
                  </li>
                ))}
                {soft.map((g) => (
                  <li key={g.field} className="mute">
                    {g.label} — {g.why}
                  </li>
                ))}
              </ul>
            </Notice>
          )}

          <ApplyLink job={j} />

          {j.description && (
            <div className="card">
              <h2 className="card-title">الوصف الوظيفي</h2>
              <p className="rc-text">{j.description}</p>
            </div>
          )}

          <BulletCard title="المهامّ الرئيسية" items={j.duties} />
          <BulletCard title="المتطلّبات التقنية" items={j.techRequirements} />
          <BulletCard title="المتطلّبات الشخصية" items={j.softRequirements} />
          <BulletCard title="المزايا" items={j.benefits} />
          <BulletCard title="اللغات" items={j.languages} />

          {/*
            ما يُورَّث إلى ملفّ من يُقبل يُعرض هنا: من يفتح الوظيفةَ يحتاج أن
            يعرف على أيّ خطّةٍ سيعمل من يُعيَّن، وعلى أيّ مركزٍ تُحمَّل كلفتُه.
          */}
          <div className="card">
            <h2 className="card-title">ما يُورَّث إلى ملفّ من يُقبل</h2>
            <div className="mt-2">
              <FactGrid>
                <Fact
                  icon="clipboard"
                  label="الوصف الوظيفي المعتمَد"
                  value={j.position?.title ?? "—"}
                  title={
                    j.position
                      ? undefined
                      : "بلا ربطٍ لا يُعرف هذا الشاغرُ من المعتمَد"
                  }
                />
                <Fact
                  icon="layers"
                  label="الدرجة"
                  value={j.grade?.name ?? "—"}
                />
                <Fact
                  icon="building"
                  label="الوحدة"
                  value={j.orgUnit?.name ?? "—"}
                />
                <Fact
                  icon="clock"
                  label="خطة العمل"
                  value={j.schedule?.name ?? "—"}
                />
                <Fact
                  icon="bank"
                  label="مركز التكلفة"
                  value={j.costCenter?.name ?? "—"}
                />
              </FactGrid>
            </div>
            <p className="tiny mute mt-2">
              تُنسخ إلى ملفّ الموظّف حين يُقبل العرضُ ويُنشأ الملفّ — فتُقاس
              حضورُه على خطّتها وتُحمَّل كلفتُه على مركزها بلا إدخالٍ ثانٍ.
            </p>
          </div>

          <div className="card">
            <h2 className="card-title">أسئلة الاستمارة</h2>
            {j.formFields.length === 0 ? (
              <p className="tiny mute">
                لا أسئلة — تُسأل الهويّة وحدها: الاسم والهاتف والبريد.{" "}
                <Link className="link" to="/settings/application-forms">
                  اضبط استمارة
                </Link>
                .
              </p>
            ) : (
              <ol className="rc-qlist">
                {j.formFields.map((f) => (
                  <li key={f.id}>
                    {f.label}
                    {f.required && <span className="rc-req"> مطلوب</span>}
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="row" style={{ gap: "var(--sp-2)" }}>
            <Link className="btn" to={`/hr/recruit/pipeline?jobPostId=${j.id}`}>
              <Icon name="funnel" size={15} />
              مسار هذه الوظيفة
            </Link>
          </div>
        </>
      )}

      {editing && j && <JobDialog job={j} onClose={() => setEditing(false)} />}
    </div>
  );
}

/** بطاقةُ قائمةٍ — لا تُرسم حين تكون فارغة، فبطاقةٌ فارغةٌ ضجيج. */
function BulletCard({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="card">
      <h2 className="card-title">{title}</h2>
      <ul className="rc-qlist">
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * رابط التقديم.
 *
 * يُعرض كاملًا لا مختصرًا: من ينسخه ينشره في إعلانٍ ويريد أن يرى ما ينشر.
 * ويُقال صراحةً حين لا يعمل — رابطٌ معروضٌ بجانب وظيفةٍ مسوّدة يُنشر ثم
 * يُفتح على «غير موجودة»، وهو أسوأ من ألّا يُعرض.
 */
function ApplyLink({ job }: { job: JobRow }) {
  const session = useSession();
  const toast = useToast();
  const url = jobApplyUrl(
    window.location.origin,
    session.tenant.slug,
    job.slug,
  );

  return (
    <div className="card">
      <h2 className="card-title">رابط التقديم</h2>

      {!job.accepting && (
        <Notice kind="warning" title="الرابط لا يقبل تقديمًا الآن" icon="alert">
          {JOB_POST_STATUS_MAP[job.status].description} من يفتحه يرى «هذه
          الوظيفة غير موجودة أو لم تعد متاحة».
        </Notice>
      )}

      <div className="rc-link">
        <code dir="ltr">{url}</code>
        <button
          className="btn btn-sm"
          onClick={() => {
            void navigator.clipboard.writeText(url);
            toast("نُسخ الرابط");
          }}
        >
          <Icon name="paperclip" size={14} />
          نسخ
        </button>
        <a className="btn btn-sm" href={url} target="_blank" rel="noreferrer">
          <Icon name="eye" size={14} />
          فتح
        </a>
      </div>

      <p className="tiny mute mt-2">
        لا يطلب حسابًا ولا كلمة سرّ. من يفتحه يملأ الاستمارة فيصل طلبُه إلى عمود
        «تقديم» في المسار.
      </p>
    </div>
  );
}

/* ==========================================================================
   محرّر البنود
   ========================================================================== */

/**
 * قائمةُ بنودٍ تُحرَّر سطرًا سطرًا.
 *
 * ولا تُكتب في مربّعٍ واحدٍ يُقسَّم بالأسطر: من يكتب في مربّعٍ يترك سطرًا
 * فارغًا بين البنود فيصير بندًا خاليًا في الإعلان، ويلصق فقرةً من مستندٍ
 * فتصير بندًا واحدًا طولُه خمسةُ أسطر. والصفُّ الواحدُ يُنتج البندَ كما
 * سيُقرأ.
 */
function BulletEditor({
  label,
  hint,
  placeholder,
  items,
  onChange,
}: {
  label: string;
  hint?: string;
  placeholder?: string;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const value = draft.trim();
    if (!value || items.length >= MAX_JOB_BULLETS) return;
    onChange([...items, value]);
    setDraft("");
  };

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [row] = next.splice(index, 1);
    next.splice(to, 0, row!);
    onChange(next);
  };

  return (
    <div className="field span-2">
      <span className="label">{label}</span>
      {hint && <span className="tiny mute">{hint}</span>}

      {items.length > 0 && (
        <ul className="rc-qlist mt-2">
          {items.map((x, i) => (
            <li key={i}>
              <span className="row" style={{ gap: 6, alignItems: "center" }}>
                <span style={{ flex: 1 }}>{x}</span>
                {/*
                  الترتيبُ معنًى لا زينة: أوّلُ مهمّةٍ في القائمة هي ما يُقرأ
                  أوّلًا في الإعلان وما يُسأل عنه أوّلًا في المقابلة.
                */}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  title="أعلى"
                >
                  <Icon name="chevronUp" size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  title="أسفل"
                >
                  <Icon name="chevronDown" size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                  title="احذف"
                >
                  <Icon name="trash" size={12} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="row mt-2" style={{ gap: "var(--sp-2)" }}>
        <input
          className="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          maxLength={400}
          aria-label={`بند جديد في ${label}`}
        />
        <button
          type="button"
          className="btn"
          onClick={add}
          disabled={draft.trim() === "" || items.length >= MAX_JOB_BULLETS}
        >
          <Icon name="plus" size={14} />
          أضف
        </button>
      </div>

      {items.length >= MAX_JOB_BULLETS && (
        <span className="tiny mute">
          بلغتَ الحدّ: {num(MAX_JOB_BULLETS)} بندًا — قائمةٌ أطولُ لا تُقرأ.
        </span>
      )}
    </div>
  );
}

/* ==========================================================================
   نافذة الوظيفة
   ========================================================================== */

function JobDialog({
  job,
  onClose,
}: {
  job: JobDetailView | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const forms = useQuery({
    queryKey: ["recruit", "forms"],
    queryFn: () => api<FormLite[]>("/settings/application-forms"),
  });
  const campaigns = useQuery({
    queryKey: ["recruit", "campaigns"],
    queryFn: () => api<CampaignLite[]>("/hr/recruit/campaigns"),
  });
  const positions = useQuery({
    queryKey: ["hr", "org", "positions"],
    queryFn: () => api<PositionLite[]>("/hr/org/positions"),
  });
  const grades = useQuery({
    queryKey: ["hr", "org", "grades"],
    queryFn: () => api<GradeLite[]>("/hr/org/grades"),
  });
  const units = useQuery({
    queryKey: ["hr", "setup", "units", "options"],
    queryFn: () => api<UnitOption[]>("/hr/setup/units/options"),
  });
  const plans = useQuery({
    queryKey: ["hr", "setup", "schedules"],
    queryFn: () => api<PlanLite[]>("/hr/setup/schedules"),
  });
  const centers = useQuery({
    queryKey: ["hr", "setup", "cost-centers"],
    queryFn: () => api<CenterLite[]>("/hr/setup/cost-centers"),
  });

  const [title, setTitle] = useState(job?.title ?? "");
  const [deptKey, setDeptKey] = useState(job?.deptKey ?? "");
  const [employmentType, setEmploymentType] = useState<EmploymentType>(
    (job?.employmentType as EmploymentType) ?? "FULL_TIME",
  );
  const [workMode, setWorkMode] = useState<WorkMode>(
    (job?.workMode as WorkMode) ?? "ONSITE",
  );
  const [location, setLocation] = useState(job?.location ?? "");
  const [openings, setOpenings] = useState(String(job?.openings ?? 1));
  const [description, setDescription] = useState(job?.description ?? "");

  const [duties, setDuties] = useState<string[]>(job?.duties ?? []);
  const [tech, setTech] = useState<string[]>(job?.techRequirements ?? []);
  const [soft, setSoft] = useState<string[]>(job?.softRequirements ?? []);
  const [benefits, setBenefits] = useState<string[]>(job?.benefits ?? []);
  const [languages, setLanguages] = useState<string[]>(job?.languages ?? []);

  const [salaryMin, setSalaryMin] = useState(
    job?.salaryMin === null || job?.salaryMin === undefined
      ? ""
      : String(job.salaryMin),
  );
  const [salaryMax, setSalaryMax] = useState(
    job?.salaryMax === null || job?.salaryMax === undefined
      ? ""
      : String(job.salaryMax),
  );
  const [showSalary, setShowSalary] = useState(job?.showSalary ?? false);
  const [experienceMin, setExperienceMin] = useState(
    job?.experienceMin === null || job?.experienceMin === undefined
      ? ""
      : String(job.experienceMin),
  );
  const [education, setEducation] = useState(job?.education ?? "");

  const [positionId, setPositionId] = useState(job?.position?.id ?? "");
  const [gradeId, setGradeId] = useState(job?.gradeId ?? "");
  const [orgUnitId, setOrgUnitId] = useState(job?.orgUnitId ?? "");
  const [scheduleId, setScheduleId] = useState(job?.scheduleId ?? "");
  const [costCenterId, setCostCenterId] = useState(job?.costCenterId ?? "");

  const [formId, setFormId] = useState(job?.form?.id ?? "");
  const [campaignId, setCampaignId] = useState(job?.campaign?.id ?? "");
  const [closesOn, setClosesOn] = useState(job?.closesOn ?? "");

  /*
   * الافتراضية تُقترح على الجديدة وحدها — تغييرُ استمارةِ وظيفةٍ قائمةٍ قرار.
   *
   * والاقتراحُ يسقط بأوّل اختيار، ولا يُقاس بفراغ الحقل: «الهويّة وحدها»
   * قيمتُها فراغ، فكان اختيارُها يُعيد الافتراضيةَ في اللحظة نفسِها —
   * وظيفةٌ أُريد لها ثلاثةُ أسئلةٍ تخرج باستمارةٍ من عشرين.
   */
  const suggestedForm = forms.data?.find((f) => f.isDefault)?.id ?? "";
  const [formPicked, setFormPicked] = useState(false);
  const chosenForm = job || formPicked ? formId : formId || suggestedForm;

  const min = salaryMin === "" ? null : Number(salaryMin);
  const max = salaryMax === "" ? null : Number(salaryMax);
  const backwards = min !== null && max !== null && max < min;
  const announcingNothing = showSalary && min === null && max === null;

  const valid = title.trim().length >= 2 && !backwards && !announcingNothing;

  /**
   * سحبُ ما في الوصف الوظيفيّ المعتمَد إلى هذا الإعلان.
   *
   * نسخٌ لا ربط: الإعلانُ يُعدَّل بعده — يُضاف إليه ما تحتاجه هذه
   * الحملةُ بعينها ويُحذف ما لا يلزم — ولو كان يقرأ الوصفَ حيًّا لتغيّر
   * إعلانٌ منشورٌ بتعديل وصفٍ في شاشةٍ أخرى. والنسخُ يُضيف ولا يمحو: ما
   * كُتب هنا يبقى، وما جاء من الوصف يُلحق به.
   */
  const pullFromPosition = useMutation({
    mutationFn: (id: string) => api<PositionFull>(`/hr/org/positions/${id}`),
    onSuccess: (p) => {
      const merge = (current: string[], incoming: string[]) => [
        ...current,
        ...incoming.filter((x) => !current.includes(x)),
      ];
      setDuties((d) => merge(d, p.duties ?? []));
      setTech((t) => merge(t, p.requirements ?? []));
      if (!description.trim() && p.purpose) setDescription(p.purpose);
      if (!deptKey && p.deptKey) setDeptKey(p.deptKey);
      if (!gradeId && p.grade) setGradeId(p.grade.id);
      if (!orgUnitId && p.orgUnit) setOrgUnitId(p.orgUnit.id);
      toast("نُسخ ما في الوصف الوظيفي");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر النسخ"),
  });

  /* نطاقُ الدرجة يُقترح ولا يُفرض: وظيفةٌ نادرةٌ تُدفع فوق درجتها. */
  const grade = (grades.data ?? []).find((g) => g.id === gradeId) ?? null;
  const suggestGrade = () => {
    if (!grade) return;
    if (grade.minSalary !== null) setSalaryMin(String(grade.minSalary));
    if (grade.maxSalary !== null) setSalaryMax(String(grade.maxSalary));
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        deptKey: deptKey || null,
        employmentType,
        workMode,
        location: location.trim() || null,
        openings: Number(openings) || 1,
        description: description.trim() || null,
        duties,
        techRequirements: tech,
        softRequirements: soft,
        benefits,
        languages,
        salaryMin: min,
        salaryMax: max,
        showSalary,
        experienceMin: experienceMin === "" ? null : Number(experienceMin),
        education: education || null,
        positionId: positionId || null,
        gradeId: gradeId || null,
        orgUnitId: orgUnitId || null,
        scheduleId: scheduleId || null,
        costCenterId: costCenterId || null,
        formId: chosenForm || null,
        campaignId: campaignId || null,
        closesOn: closesOn || null,
      };
      return job
        ? api(`/hr/recruit/jobs/${job.id}`, { method: "PATCH", body })
        : api("/hr/recruit/jobs", { method: "POST", body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recruit"] });
      toast(job ? "حُفظت الوظيفة" : "أُنشئت الوظيفة مسوّدةً");
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  return (
    <Modal
      title={job ? `تعديل ${job.title}` : "وظيفة جديدة"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="job-title">
            المسمّى الوظيفي
          </label>
          <input
            id="job-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="مصمّم جرافيك"
          />
        </div>

        {/* ---------- ما يُشتقّ منه ---------- */}

        <div className="field span-2">
          <label className="label" htmlFor="job-position">
            الوصف الوظيفي المعتمَد
          </label>
          <div className="row" style={{ gap: "var(--sp-2)" }}>
            <select
              id="job-position"
              className="select"
              value={positionId}
              onChange={(e) => setPositionId(e.target.value)}
              style={{ flex: 1 }}
            >
              <option value="">بلا ربط</option>
              {(positions.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                  {p.department ? ` — ${p.department}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => positionId && pullFromPosition.mutate(positionId)}
              disabled={!positionId || pullFromPosition.isPending}
            >
              <Icon name="download" size={14} />
              انسخ منه
            </button>
          </div>
          {/*
            الربطُ ليس تكرارًا: الوصفُ يقول «ما هذه الوظيفة» ويبقى، والإعلانُ
            يقول «نبحث عن واحدٍ لها الآن» ويُغلق. وبه يُطابَق الشاغرُ بالمعتمَد.
          */}
          <span className="tiny mute">
            به يُعرف هذا الشاغرُ من المعتمَد في تخطيط القوى العاملة — كان
            يُطابَق بتشابه العنوان، فـ«مصمّم جرافيك أول» و«مصمم جرافيك أوّل»
            وظيفتان. و«انسخ منه» يُلحق مهامَّه ومتطلّباتِه بما هنا ولا يمحوه.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-dept">
            القسم
          </label>
          <select
            id="job-dept"
            className="select"
            value={deptKey}
            onChange={(e) => setDeptKey(e.target.value)}
          >
            <option value="">بلا قسم</option>
            {DEPARTMENT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-type">
            نوع التعاقد
          </label>
          <select
            id="job-type"
            className="select"
            value={employmentType}
            onChange={(e) =>
              setEmploymentType(e.target.value as EmploymentType)
            }
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {EMPLOYMENT_TYPE_MAP[t].label}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-mode">
            نمط العمل
          </label>
          <select
            id="job-mode"
            className="select"
            value={workMode}
            onChange={(e) => setWorkMode(e.target.value as WorkMode)}
          >
            {WORK_MODES.map((m) => (
              <option key={m} value={m}>
                {WORK_MODE_MAP[m].label}
              </option>
            ))}
          </select>
          {/* «دوام كامل» نوعُ تعاقد و«عن بُعد» مكانُ عمل — ومن يعمل كاملًا من بيته يحتاج الاثنين. */}
          <span className="tiny mute">
            {WORK_MODE_MAP[workMode].description}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-loc">
            مكان العمل
          </label>
          <input
            id="job-loc"
            className="input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="المقرّ — المعادي"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="job-openings">
            عدد الشواغر
          </label>
          <input
            id="job-openings"
            className="input num"
            type="number"
            min={1}
            value={openings}
            onChange={(e) => setOpenings(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="job-closes">
            تُغلق في
          </label>
          <input
            id="job-closes"
            className="input num"
            type="date"
            value={closesOn}
            onChange={(e) => setClosesOn(e.target.value)}
          />
        </div>

        {/* ---------- الوصف والمهامّ ---------- */}

        <div className="field span-2">
          <label className="label req" htmlFor="job-desc">
            الوصف الوظيفي
          </label>
          <textarea
            id="job-desc"
            className="input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="ما الذي سيفعله من يشغل هذه الوظيفة، ولماذا توجد."
          />
          <span className="tiny mute">
            لا تُنشر الوظيفةُ بدونه: من يقرأ الإعلانَ يتقدّم على الاسم وحدَه.
          </span>
        </div>

        <BulletEditor
          label="المهامّ الرئيسية"
          hint="بندًا بندًا — وترتيبُها معنًى: أوّلُها ما يُسأل عنه أوّلًا في المقابلة."
          placeholder="يضع الهويّة البصرية للحملات"
          items={duties}
          onChange={setDuties}
        />

        <BulletEditor
          label="المتطلّبات التقنية"
          hint="ما يُقاس بامتحانٍ أو بشهادة — أدواتٌ ولغاتٌ وسنواتُ خبرةٍ في مجال."
          placeholder="إتقان أدوبي إلستريتور"
          items={tech}
          onChange={setTech}
        />

        <BulletEditor
          label="المتطلّبات الشخصية"
          hint="ما يُقاس بمقابلةٍ لا بامتحان — وبلا كتابتها يختلف مُقابلان في المعيار."
          placeholder="يتقبّل الملاحظات ويعيد العمل"
          items={soft}
          onChange={setSoft}
        />

        <BulletEditor
          label="المزايا"
          hint="ما يُعرض غيرَ الراتب — وهو ما يرجّح كفّةَ عرضٍ على عرض."
          placeholder="تأمين طبّي للموظّف وأسرته"
          items={benefits}
          onChange={setBenefits}
        />

        <BulletEditor
          label="اللغات"
          placeholder="الإنجليزية — قراءةً وكتابةً"
          items={languages}
          onChange={setLanguages}
        />

        {/* ---------- الراتب والشروط ---------- */}

        <div className="field">
          <label className="label" htmlFor="job-smin">
            أدنى نطاق الراتب
          </label>
          <input
            id="job-smin"
            className="input num"
            type="number"
            min={0}
            value={salaryMin}
            onChange={(e) => setSalaryMin(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="job-smax">
            أعلى نطاق الراتب
          </label>
          <input
            id="job-smax"
            className="input num"
            type="number"
            min={0}
            value={salaryMax}
            onChange={(e) => setSalaryMax(e.target.value)}
          />
          {backwards && (
            <span className="tiny" style={{ color: "var(--danger)" }}>
              أعلى النطاق أقلّ من أدناه.
            </span>
          )}
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={showSalary}
              onChange={(e) => setShowSalary(e.target.checked)}
            />
            أعلِن النطاق في صفحة التقديم
          </label>
          {/*
            القراران منفصلان: يُكتب دائمًا لتقارن الشركةُ ما تدفع بالسوق
            ولتملأ عرضَ العمل، ويُعلَن باختيار. وحقلٌ واحدٌ يجمعهما كان
            يُجبر من لا يعلن على ألّا يكتب.
          */}
          <span className="tiny mute">
            يُكتب دائمًا ويُعلَن باختيار: منه يُملأ عرضُ العمل وتُقارن الشركةُ
            ما تدفع بالسوق، ومن لا يعلن يبقى الرقمُ عنده داخليًّا — لا يُرسَل
            إلى الصفحة أصلًا.
          </span>
          {announcingNothing && (
            <span className="tiny" style={{ color: "var(--danger)" }}>
              لا يُعلَن نطاقٌ لم يُكتب — اكتب حدًّا واحدًا على الأقلّ.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="job-exp">
            أقلّ خبرة بالسنوات
          </label>
          <input
            id="job-exp"
            className="input num"
            type="number"
            min={0}
            max={60}
            value={experienceMin}
            onChange={(e) => setExperienceMin(e.target.value)}
            placeholder="لا تُشترط"
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="job-edu">
            المؤهّل المطلوب
          </label>
          <select
            id="job-edu"
            className="select"
            value={education}
            onChange={(e) => setEducation(e.target.value)}
          >
            <option value="">لم يُحدَّد</option>
            {EDUCATION_LEVELS.map((l) => (
              <option key={l} value={l}>
                {EDUCATION_LEVEL_MAP[l as EducationLevel].label}
              </option>
            ))}
          </select>
          {/* «بكالوريوس» تعني بكالوريوسَ فما فوق — فمن يحمل ماجستيرًا لا يُردّ. */}
          <span className="tiny mute">
            تعني «فما فوق» — سلّمٌ مرتّبٌ لا خانةٌ تُطابَق.
          </span>
        </div>

        {/* ---------- ما يُورَّث إلى ملفّ من يُقبل ---------- */}

        <div className="field span-2">
          <h3 className="label" style={{ marginBottom: 0 }}>
            ما يُورَّث إلى ملفّ من يُقبل
          </h3>
          <span className="tiny mute">
            يُنسخ إلى ملفّ الموظّف حين يُقبل العرضُ ويُنشأ الملفّ — فتُقاس
            حضورُه على خطّتها وتُحمَّل كلفتُه على مركزها بلا إدخالٍ ثانٍ.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-grade">
            الدرجة
          </label>
          <div className="row" style={{ gap: "var(--sp-2)" }}>
            <select
              id="job-grade"
              className="select"
              value={gradeId}
              onChange={(e) => setGradeId(e.target.value)}
              style={{ flex: 1 }}
            >
              <option value="">بلا درجة</option>
              {(grades.data ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm"
              onClick={suggestGrade}
              disabled={
                !grade || (grade.minSalary === null && grade.maxSalary === null)
              }
              title="انسخ نطاق الدرجة إلى نطاق هذه الوظيفة"
            >
              نطاقها
            </button>
          </div>
          {grade && (
            <span className="tiny mute">
              نطاقُ الدرجة: {salaryText(grade.minSalary, grade.maxSalary)} —
              يُقترح ولا يُفرض.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="job-unit">
            الوحدة
          </label>
          <select
            id="job-unit"
            className="select"
            value={orgUnitId}
            onChange={(e) => setOrgUnitId(e.target.value)}
          >
            <option value="">بلا وحدة</option>
            {(units.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.path}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-plan">
            خطة العمل
          </label>
          <select
            id="job-plan"
            className="select"
            value={scheduleId}
            onChange={(e) => setScheduleId(e.target.value)}
          >
            <option value="">بلا خطة</option>
            {(plans.data ?? [])
              .filter((p) => p.isActive)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isDefault ? " — الافتراضية" : ""}
                </option>
              ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-cc">
            مركز التكلفة
          </label>
          <select
            id="job-cc"
            className="select"
            value={costCenterId}
            onChange={(e) => setCostCenterId(e.target.value)}
          >
            <option value="">بلا مركز</option>
            {(centers.data ?? [])
              .filter((c) => c.isActive)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
        </div>

        {/* ---------- التقديم ---------- */}

        <div className="field">
          <label className="label" htmlFor="job-form">
            استمارة التقديم
          </label>
          <select
            id="job-form"
            className="select"
            value={chosenForm}
            onChange={(e) => {
              setFormPicked(true);
              setFormId(e.target.value);
            }}
          >
            <option value="">الهويّة وحدها</option>
            {(forms.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
                {f.isDefault ? " — الافتراضية" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="job-campaign">
            الحملة
          </label>
          <select
            id="job-campaign"
            className="select"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">بلا حملة</option>
            {(campaigns.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!job && (
        <p className="tiny mute mt-2">
          تُحفظ مسوّدةً — رابط التقديم لا يعمل حتى تُنشر، والنشرُ يحتاج وصفًا
          ومهامَّ ومتطلّباتٍ تقنيّة.
        </p>
      )}
    </Modal>
  );
}
